import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, Paper, Chip,
} from '@mui/material';
import { Agriculture, Add, Receipt, RequestPage, Warning } from '@mui/icons-material';
import { TaskAlertCard } from '../../components/AlertCards';
import { useQuery } from '@tanstack/react-query';
import {
  dashboardService, farmOperationService, expenseService,
  type TaskDueAlert,
} from '@smartgrow/shared';

export const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const userData = localStorage.getItem('user_data');
  const user = userData ? JSON.parse(userData) : null;

  const { data: taskAlerts = [] } = useQuery<TaskDueAlert[]>({
    queryKey: ['taskDueAlerts'],
    queryFn: dashboardService.getTaskDueAlerts,
    placeholderData: [],
  });

  const { data: farmOpsData } = useQuery({
    queryKey: ['farmOperations'],
    queryFn: () => farmOperationService.getAll(0, 10),
  });

  const { data: expensesData } = useQuery({
    queryKey: ['myExpenses'],
    queryFn: () => expenseService.getMyExpenses(0, 10),
  });

  const activeOps = farmOpsData?.content?.filter((op: any) => op.status === 'ACTIVE') ?? [];
  const myExpenses = expensesData?.content?.filter((e: any) => e.status === 'PENDING') ?? [];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Supervisor Dashboard</Typography>
          <Typography variant="body1" color="text.secondary">Welcome, {user?.fullName}. Here's your field overview.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/farm/new')}>New Operation</Button>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard/farm')}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Active Farm Ops</Typography>
                  <Typography variant="h4" fontWeight="bold">{activeOps.length}</Typography>
                </Box>
                <Box sx={{ bgcolor: '#558B2F', borderRadius: 2, p: 1.5 }}>
                  <Agriculture sx={{ color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard/expenses')}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Pending Expenses</Typography>
                  <Typography variant="h4" fontWeight="bold">{myExpenses.length}</Typography>
                </Box>
                <Box sx={{ bgcolor: '#E65100', borderRadius: 2, p: 1.5 }}>
                  <Receipt sx={{ color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard/payments/requests')}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Task Alerts</Typography>
                  <Typography variant="h4" fontWeight="bold">{taskAlerts.length}</Typography>
                </Box>
                <Box sx={{ bgcolor: '#D32F2F', borderRadius: 2, p: 1.5 }}>
                  <Warning sx={{ color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Active farm operations */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Active Farm Operations</Typography>
              <Button size="small" onClick={() => navigate('/dashboard/farm')}>View All</Button>
            </Box>
            {activeOps.length === 0
              ? <Typography variant="body2" color="text.secondary">No active operations.</Typography>
              : activeOps.slice(0, 5).map((op: any) => (
                <Box
                  key={op.id}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard/farm/${op.id}`)}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{op.operationType?.replaceAll('_', ' ')}</Typography>
                    <Typography variant="caption" color="text.secondary">{op.cropVariety || op.animalType || '—'}</Typography>
                  </Box>
                  <Chip label={op.status} color="success" size="small" />
                </Box>
              ))}
          </Paper>

          {/* Task alerts */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Task Alerts</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {taskAlerts.length === 0
                ? <Typography variant="body2" color="text.secondary">No tasks approaching due date or overdue.</Typography>
                : taskAlerts.map((a) => (
                  <TaskAlertCard
                    key={`${a.taskId}-${a.severity}`}
                    alert={a}
                    onClick={() => navigate(`/dashboard/farm/${a.operationId}`)}
                  />
                ))}
            </Box>
          </Paper>
        </Grid>

        {/* Right: quick actions + pending expenses */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button variant="outlined" fullWidth startIcon={<Add />} onClick={() => navigate('/dashboard/farm/new')}>New Farm Operation</Button>
              <Button variant="outlined" fullWidth startIcon={<Receipt />} onClick={() => navigate('/dashboard/expenses/new')}>Submit Expense</Button>
              <Button variant="outlined" fullWidth startIcon={<RequestPage />} onClick={() => navigate('/dashboard/payments/requests/new')}>Payment Request</Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">My Pending Expenses</Typography>
              <Button size="small" onClick={() => navigate('/dashboard/expenses')}>View All</Button>
            </Box>
            {myExpenses.length === 0
              ? <Typography variant="body2" color="text.secondary">No pending expense requests.</Typography>
              : myExpenses.slice(0, 5).map((e: any) => (
                <Box key={e.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">₦{Number(e.amount).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{e.description?.slice(0, 40)}…</Typography>
                  </Box>
                  <Chip label="PENDING" color="warning" size="small" />
                </Box>
              ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
